import portalSchema, { components } from 'support/types/schema-portal';
import { TradePartnerStats, TransactionGraphData } from 'pages/Dashboard/types';

import { ValueOf } from '.';

export type RelationshipActionRequired = NonNullable<
  NonNullable<portalSchema.paths['/v1/connections']['get']['parameters']['query']>['filter[action_required]']
>[number];

export enum DocumentType {
  unknown = 'UNKNOWN',
  order = 'ORDER',
  orderResponse = 'ORDER_RESPONSE',
  shippingNotice = 'SHIPPING_NOTICE',
  invoice = 'INVOICE',
  creditNote = 'CREDIT_NOTE',
  dispatchInstruction = 'DISPATCH_INSTRUCTION',
  dispatchResponse = 'DISPATCH_INSTRUCTION_RESPONSE',
  receivalNotice = 'RECEIVAL_NOTICE',
  remittanceAdvice = 'REMITTANCE_ADVICE',
  productCatalog = 'PRODUCT_CATALOG',
  acknowledgement = 'ACKNOWLEDGEMENT',
  quote = 'QUOTE',
  inventoryReport = 'INVENTORY_REPORT',
  salesReport = 'SALES_REPORT',
}

export enum DocumentTypeWithVirtual {
  unknown = 'UNKNOWN',
  order = 'ORDER',
  orderResponse = 'ORDER_RESPONSE',
  shippingNotice = 'SHIPPING_NOTICE',
  invoice = 'INVOICE',
  creditNote = 'CREDIT_NOTE',
  dispatchInstruction = 'DISPATCH_INSTRUCTION',
  dispatchResponse = 'DISPATCH_INSTRUCTION_RESPONSE',
  receivalNotice = 'RECEIVAL_NOTICE',
  remittanceAdvice = 'REMITTANCE_ADVICE',
  productCatalog = 'PRODUCT_CATALOG',
  acknowledgement = 'ACKNOWLEDGEMENT',
  quote = 'QUOTE',
  inventoryReport = 'INVENTORY_REPORT',
  salesReport = 'SALES_REPORT',
  //Virtual document types
  orderConfirmation = 'ORDER_CONFIRMATION',
  partialOrderConfirmation = 'PARTIAL_ORDER_CONFIRMATION',
  orderRejection = 'ORDER_REJECTION',
  serviceInvoice = 'SERVICE_INVOICE',
  debitNote = 'DEBIT_NOTE',
}

export enum TransactionErrorType {
  internal = 'INTERNAL',
  config = 'CONFIG',
  data = 'DATA',
  notice = 'NOTICE',
}

export enum MessageState {
  canonicalized = 'canonicalized',
  decanonicalizedFailed = 'decanonicalization_failed',
  decanonicalized = 'decanonicalized',
  dropped = 'dropped',
  enriched = 'enriched',
  enrichedFailed = 'enrichment_failed',
  initial = 'initial',
  pendingCollection = 'pending_collection',
  pendingRelease = 'pending_release',
  pendingReview = 'pending_review',
  superseded = 'superseded',
  routed = 'routed',
  routingFailed = 'routing_failed',
  validated_for_destination = 'validated_for_destination',
  validation_for_destination_failed = 'validation_for_destination_failed',
  post_processed = 'post_processed',
  postProcessingFailed = 'post_processing_failed',
}

export enum SenderEnvelopeState {
  pendingAutoRecovery = 'pending_auto_recovery',
  canonicalizationFailed = 'canonicalization_failed',
  canonicalized = 'canonicalized',
  dropped = 'dropped',
  initial = 'initial',
  received = 'received',
  normalized = 'normalized',
  receivalFailed = 'receival_failed',
  normalizationFailed = 'normalization_failed',
  enrichementFailed = 'enrichment_failed',
  routingFailed = 'routing_failed',
}

export enum ReceiverEnvelopeState {
  pendingAutoRecovery = 'pending_auto_recovery',
  alreadySent = 'already_sent',
  decanonicalized = 'decanonicalized',
  delivered = 'delivered',
  deliveryFailed = 'delivery_failed',
  deliveryPending = 'delivery_pending',
  deliverySimulated = 'delivery_simulated',
  deliverySimulationFailed = 'delivery_simulation_failed',
  dropped = 'dropped',
  initial = 'initial',
  pendingPickup = 'pending_pickup',
  pickupFailed = 'pickup_failed',
  denormalized = 'denormalized',
  denormalizationFailed = 'denormalization_failed',
  manuallyDelivered = 'manually_delivered',
}

export const InternalTransactionState = {
  ...SenderEnvelopeState,
  ...MessageState,
  ...ReceiverEnvelopeState,
};
export type InternalTransactionStateType = ValueOf<
  (typeof InternalTransactionState)[keyof typeof InternalTransactionState]
>;

export type ErrorTypeEnum = 'INTERNAL' | 'CONFIG' | 'DATA' | 'NOTICE';

export type AddressIdDomain = portalSchema.components['schemas']['AddressIdDomain'];

export type UnitOfMeasure = 'EA' | 'GRM' | 'HUR' | 'KGM' | 'LTR' | 'MLT' | 'MTK' | 'MTR' | 'PCK' | 'SET';

export enum TransactionState {
  failed = 'FAILED',
  success = 'SUCCESS',
  pending = 'PENDING',
  dropped = 'DROPPED',
  unknown = 'UNKNOWN',
  missing = 'MISSING',
  pending_review = 'PENDING_REVIEW',
  superseded = 'SUPERSEDED',
}

export enum TransactionFlowState {
  live = 'LIVE',
  test = 'TEST',
}

export enum RelationshipPartnerStatus {
  connected = 'connected',
  pending = 'pending',
  notConnected = 'not_connected',
}
export enum RelationshipStatus {
  TASKS_PENDING = 'TASKS_PENDING',
  PARTNER_APPROVAL_PENDING = 'PARTNER_APPROVAL_PENDING',
  PARTNER_ONBOARDING_PENDING = 'PARTNER_ONBOARDING_PENDING',
  PARTNER_INTEGRATION_PENDING = 'PARTNER_INTEGRATION_PENDING',
  PARTNER_PROCESS_PENDING = 'PARTNER_PROCESS_PENDING',
  ISSUE_RESOLVER_PENDING = 'ISSUE_RESOLVER_PENDING',
  LIVE_ORDER_NEEDED = 'LIVE_ORDER_NEEDED',
  READY_FOR_TESTING = 'READY_FOR_TESTING',
  HAS_TEST_MESSAGES = 'HAS_TEST_MESSAGES',
  IN_REVIEW = 'IN_REVIEW',
  GO_LIVE_DATE_PENDING = 'GO_LIVE_DATE_PENDING',
  GO_LIVE_DATE_SCHEDULED = 'GO_LIVE_DATE_SCHEDULED',
  ACTIVE = 'ACTIVE',
  LIVE = 'LIVE',
  REJECTED = 'REJECTED',
  DEACTIVATED = 'DEACTIVATED',
  BLOCKED = 'BLOCKED',
}

/**
 * @deprecated Please use RelationshipStatus instead
 *
 * This enum was introduced during the Post-migration cleanup project.
 * Previously, we used customer_status to identify relationships that were not yet Ready (In testing, Requested) for displaying onboarding on the welcome screen.
 * Since we removed customer_status due to outdated logic, we passed the relationship state to the FE for filtering via this endpoint.
 *
 * Most relationships are now going to Ready due to the frictionless onboarding, and almost no one is going through the Onboarding screen, this should be removed soon.
 * I've marked it as deprecated because the FE should rely on a single status (RelationshipDocumentStatus) to avoid confusion.
 * But this was the quickest solution to avoid disrupting the flow.
 */
export enum RelationshipState {
  REQUESTED = 'REQUESTED',
  IN_TESTING = 'IN_TESTING',
  READY = 'READY',
  REJECTED = 'REJECTED',
  DEACTIVATED = 'DEACTIVATED',
  BLOCKED = 'BLOCKED',
}

export enum TransactionInternalType {
  message = 'message',
  senderEnvelope = 'sender-envelope',
}

export type ItemIdentifier = {
  domain: 'GS1' | 'BUYER' | 'SUPPLIER';
  identifier: string;
};
export type ItemIdentifierDomain = ItemIdentifier['domain'];

export type ModificationDTO = {
  type: 'ALLOWANCE' | 'CHARGE';
  reasonCode?: string;
  amount?: number;
  percentage?: number;
  tax?: {
    amount?: number;
    percentage?: number;
  };
};

export type ModificationType = ModificationDTO['type'];

export type ModificationReasonCode = 'SHIPPING' | 'HANDLING' | 'DISCOUNT' | 'PACKAGING' | 'INSURANCE' | 'ROUNDING';

export type ModificationGroupDTO = {
  level: number;
  basis?: number;
  modifications: Array<ModificationDTO>;
};

export type InvoiceItemDTO = {
  quantity?: number;
  unitPrice?: number;
  modificationGroups?: Array<ModificationGroupDTO>;
  isDepositItem?: boolean;
};

export type PaymentTermDTO = components['schemas']['PricingConfigPaymentTerms'];

export type MessageDTO = portalSchema.components['schemas']['Message'];

export type MessageV2DTO = portalSchema.components['schemas']['MessageV2'];

//Not perfect, but we don't have the origin app types any other way
export type TransactionOriginApp =
  portalSchema.paths['/v2/messages/{messageId}']['get']['responses']['200']['content']['application/json']['origin_app'];

export type NewTransactionDTO<T> = {
  type: DocumentType;
  content: T;
};

export type RawTransactionDTO = portalSchema.components['schemas']['TransactionListItem'];
export type TransactionDTO = RawTransactionDTO & {
  internalType: TransactionInternalType;
};

export type SenderEnvelopeDTO = portalSchema.components['schemas']['SenderEnvelope'];
export type SenderEnvelopeV2DTO = portalSchema.components['schemas']['SenderEnvelopeV2'];

export type ReceiverEnvelopeDTO = portalSchema.components['schemas']['ReceiverEnvelope'];

export type IntegrationDTO = portalSchema.components['schemas']['Integration'];
export type IntegrationConnectivityTestDTO = portalSchema.components['schemas']['ConnectivityTest'];

export type IntegrationProcessDTO = portalSchema.components['schemas']['Process'];
export type IntegrationProcessTestDTO = portalSchema.components['schemas']['ProcessTest'];

export type AS2ConfigurationDTO = portalSchema.components['schemas']['AS2Configuration'];

export type EDIFACTSyntaxIdentifier =
  portalSchema.components['schemas']['AS2Configuration']['edifact_syntax_identifier'];

export type EDIFACTSyntaxVersion = portalSchema.components['schemas']['AS2Configuration']['edifact_syntax_version'];

export type RelationshipDTO = portalSchema.components['schemas']['Relationship'];
export type RelationshipV2 = portalSchema.components['schemas']['RelationshipV2'];
export type RelationshipDocumentStatus = portalSchema.components['schemas']['RelationshipDocumentStatus'];

export type UserDTO = portalSchema.components['schemas']['User'];
export type VersionLogDTO = portalSchema.components['schemas']['VersionLog'];

export type PartnerDTO = portalSchema.components['schemas']['Partner'];
export type PartnerV2 = portalSchema.components['schemas']['PartnerV2'];

export type PartnerDocumentTypes = portalSchema.components['schemas']['PartnerDocumentTypes'];
export type PartnerDocumentTypeItem = portalSchema.components['schemas']['PartnerDocumentTypeItem'];

export type PartnerSettingsDTO = portalSchema.components['schemas']['PartnerSettings'];

export type InviteMemberDTO = portalSchema.components['schemas']['InviteMember'];
export type UpdatePartnerDTO = portalSchema.components['schemas']['UpdatePartner'];

export type InboxStatsDTO = portalSchema.components['schemas']['InboxStats'];

export type PricingConfigDTO = portalSchema.components['schemas']['PricingConfig'];
export type PricingConfigPaymentTermsDTO = portalSchema.components['schemas']['PricingConfigPaymentTerms'];
export type PricingConfigPayloadLineItemDTO = portalSchema.components['schemas']['PricingConfigPayloadLineItem'];
export type PricingConfigModificationDTO = portalSchema.components['schemas']['PricingConfigModification'];

export type FailedTransactionCountDTO = {
  failedTransactions: number;
};

export type DashboardStatsDTO = {
  transactions: TransactionGraphData;
  tradePartnerStats: TradePartnerStats;
};

export type PostalAddress = {
  name: string | null;
  street: string;
  city: string;
  postalCode: string | null;
  country: string | null;
  addressExtra: string | null;
};

export type Address = {
  gln: string;
  name: string;
  postalAddress: PostalAddress | null;
};

export type PaginationDTO<ItemType> = {
  totalCount?: number | null;
  count: number;
  hasMore: boolean;
  currentCursor: string;
  nextCursor: string;
  previousCursor: string;
  nextPageUrl?: string;
  perPage: number;
  items: Array<ItemType>;
};

export type TransactionError = {
  id: string;
  type: ValueOf<TransactionErrorType>;
  title: string | null;
  description?: string | null;
  howToFix?: string | null;
};

export enum ProcessDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

export enum EdifactStandard {
  D01B = 'D01B',
  D96A = 'D96A',
}

export enum IOnboardingStepState {
  NOT_STARTED = 'NOT_STARTED',
  WAITING_FOR_PROCUROS = 'WAITING_FOR_PROCUROS',
  COMPLETED = 'COMPLETED',
}

export enum IOnboardingStepKeys {
  CREATE_COMPANY = 'CREATE_COMPANY',
  INVITE_YOUR_TEAM_MEMBERS = 'INVITE_YOUR_TEAM_MEMBERS',
  SETUP_INTEGRATION = 'SETUP_INTEGRATION',
  CREATE_OR_UPLOAD_PRICE_LIST = 'CREATE_OR_UPLOAD_PRICE_LIST',
}

export type ConnectorsType = portalSchema.components['schemas']['ConnectorEnum'];
export enum Connectors {
  API_V2 = 'API_V2',
  AS2 = 'AS2',
  SFTP = 'SFTP',
  WECLAPP = 'WECLAPP',
  XENTRAL_V3 = 'XENTRAL_V3',
  WEB_EDI = 'WEB_EDI',
  MICROSOFT_BUSINESS_CENTRAL = 'MICROSOFT_BUSINESS_CENTRAL',
  ODOO = 'ODOO',
  HAUFE = 'HAUFE',
  NONE = 'NONE',
}

export enum IdentifierDomainOptions {
  GS1 = 'GS1',
  RECEIVER_INTERNAL = 'RECEIVER_INTERNAL',
}

type ProcessDTO = portalSchema.components['schemas']['Process'];
type CanonicalFieldResponseDTO = portalSchema.components['schemas']['CanonicalFieldResponse'];

export type ProcessSpecificationDTO = {
  process: ProcessDTO | null;
  fields: Array<CanonicalFieldResponseDTO>;
  data: Record<string, unknown> | null | undefined;
  meta?: portalSchema.components['schemas']['ProcessSpecificationMeta'];
  updated_at?: string;
};

export enum IssueResolverType {
  CURRENT_PARTNER = 'CURRENT_PARTNER',
  OTHER_PARTNER = 'OTHER_PARTNER',
  PROCUROS = 'PROCUROS',
}

export type InvoiceVerificationDTO = portalSchema.components['schemas']['InvoiceVerificationResource'];

export enum PaymentReconciliationStatusEnum {
  OPEN = 'OPEN',
  COMPLETED = 'COMPLETED',
}

export type ProcessCanonicalField = components['schemas']['CanonicalFieldResponse'];

export enum CompanyType {
  BUYER = 'BUYER',
  SUPPLIER = 'SUPPLIER',
}

export enum PartnerType {
  NETWORK_MEMBER = 'NETWORK_MEMBER',
  PROCUROS_CUSTOMER = 'PROCUROS_CUSTOMER',
}

export type OrderFormDTO = components['schemas']['OrderForm'];
export type OrderFormCategoryDTO = components['schemas']['Category'];
export type OrderFormProductDTO = components['schemas']['Product'];

export type PartyDTO = components['schemas']['Party'];
export type PartyIdentifierDTO = components['schemas']['PartyIdentifier'];

export type TaskBaseDTO = components['schemas']['TaskBase'];
export type TaskDTO = components['schemas']['Task'];
